import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Compare from "./Compare";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_SECRET_KEY = process.env.REACT_APP_API_SECRET_KEY;

function Viewer() {
  const { tenant_id, entity_id } = useParams();
  const location = useLocation();

  const [rows, setRows] = useState([]);

  /* ---------------- FILTER STATE ---------------- */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [actionType, setActionType] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: "",
    toDate: "",
    actionType: "",
  });

  const [isSearchApplied, setIsSearchApplied] = useState(false);

  /* ---------------- TABLE STATE ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [sortAsc, setSortAsc] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  /* ---------------- COMPARE MODAL STATE ---------------- */
  const [showCompare, setShowCompare] = useState(false);
  const [compareRow, setCompareRow] = useState(null);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    let url = "";

    // Build URL based on route
    if (location.pathname.startsWith("/audit-log")) {
      if (tenant_id && entity_id) {
        url = `${API_BASE_URL}/member/audit-log/${tenant_id}/${entity_id}`;
      } else if (tenant_id) {
        url = `${API_BASE_URL}/member/audit-log/${tenant_id}`;
      } else {
        url = `${API_BASE_URL}/member/audit-log`;
      }
    } else if (location.pathname.startsWith("/session")) {
      if (tenant_id && entity_id) {
        url = `${API_BASE_URL}/member/session/${tenant_id}/${entity_id}`;
      } else if (tenant_id) {
        url = `${API_BASE_URL}/member/session/${tenant_id}`;
      } else {
        url = `${API_BASE_URL}/member/session`;
      }
    }

    if (!url) return;

    // Fetch call with API KEY in HEADERS
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "api-key": API_SECRET_KEY 
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error("Server error or Unauthorized");
        return res.json();
      })
      .then((data) => {
        if (data.status === 1 && data.data) {
          setRows(data.data);
        } else {
          setRows([]);
        }
      })
      .catch((err) => {
        setRows([]);
        console.error("Fetch Error:", err);
        alert("Failed to fetch data. Please check API Key or Connection.");
      });
  }, [tenant_id, entity_id, location.pathname]);

  /* ---------------- FILTER LOGIC ---------------- */
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const createdDate = row.created_at ? new Date(row.created_at) : null;

      const fromMatch = appliedFilters.fromDate
        ? createdDate >= new Date(appliedFilters.fromDate)
        : true;

      const toMatch = appliedFilters.toDate
        ? createdDate <= new Date(`${appliedFilters.toDate}T23:59:59`)
        : true;

      const actionMatch = appliedFilters.actionType
        ? row.action_type === appliedFilters.actionType
        : true;

      return fromMatch && toMatch && actionMatch;
    });
  }, [rows, appliedFilters]);

  /* ---------------- SORT LOGIC ---------------- */
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const d1 = new Date(a.created_at || 0);
      const d2 = new Date(b.created_at || 0);
      return sortAsc ? d1 - d2 : d2 - d1;
    });
  }, [filteredRows, sortAsc]);

  /* ---------------- PAGINATION LOGIC ---------------- */
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const startEntry = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, sortedRows.length);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  /* ---------------- ACTIONS ---------------- */
  const handleSearch = () => {
    setAppliedFilters({ fromDate, toDate, actionType });
    setCurrentPage(1);
    setIsSearchApplied(true);
  };

  const handleCancel = () => {
    setFromDate("");
    setToDate("");
    setActionType("");
    setAppliedFilters({ fromDate: "", toDate: "", actionType: "" });
    setCurrentPage(1);
    setIsSearchApplied(false);
  };

  const handleExport = () => {
    if (!sortedRows.length) return;
    const excelData = sortedRows.map((row, index) => ({
      "S.No": index + 1,
      Date: row.created_at || "",
      "Action Taken By": row.action_by?.member_name || "",
      "Action Type": row.action_type || "",
      Description: row.action_name || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "AuditReport.xlsx");
  };

  return (
    <div className="container-fluid py-3">
      {/* 🔹 TOP CONTROLS: Alignment Fixed */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-1 bg-light p-1 rounded shadow-sm">
        
        {/* Left Side: Page Size */}
        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary small fw-bold">Show</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 15, 20, 25, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="text-secondary small fw-bold">entries</span>
        </div>

        {/* Right Side: Filters */}
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-dark mb-0">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={isSearchApplied}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-dark mb-0">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={isSearchApplied}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-dark mb-0">Type</label>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: "110px" }}
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              disabled={isSearchApplied}
            >
              <option value="">All Types</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
            </select>
          </div>

          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm px-3 fw-bold ${isSearchApplied ? "btn-danger" : "btn-primary"}`}
              onClick={isSearchApplied ? handleCancel : handleSearch}
            >
              {isSearchApplied ? "Clear" : "Search"}
            </button>
            <button className="btn btn-sm btn-success fw-bold" onClick={handleExport}>
              <i className="fa-solid fa-file-excel me-1"></i> Export
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 TABLE */}
      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 rounded">
          <thead>
            <tr>
              <th
                className="ps-3 rounded-start"
                style={{ backgroundColor: "#e2e8f0" }}
              >
                S.No
              </th>

              <th
                style={{ backgroundColor: "#e2e8f0", cursor: "pointer" }}
                onClick={() => setSortAsc(!sortAsc)}
              >
                Date {sortAsc ? "▲" : "▼"}
              </th>

              <th style={{ backgroundColor: "#e2e8f0" }}>Action Taken By</th>
              <th style={{ backgroundColor: "#e2e8f0" }}>Action Type</th>
              <th style={{ backgroundColor: "#e2e8f0" }}>Description</th>

              <th
                className="text-center rounded-end"
                style={{ backgroundColor: "#e2e8f0" }}
              >
                Action
              </th>
            </tr>

          </thead>

            <tbody>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">No data found</td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => (
                  <tr key={index}>
                    <td className="ps-3">{startEntry + index}</td>
                    <td className="text-nowrap">{row.created_at || "-"}</td>
                    <td>
                      {row.tenant_domain && row.action_by?.member_id ? (
                        <a
                          href={`${row.tenant_domain}/portal/members/member-details/${row.action_by.member_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none fw-medium"
                        >
                          {row.action_by.member_name}
                        </a>
                      ) : (
                        row.action_by?.member_name || "-"
                      )}
                    </td>
                    <td>
                      <span className={`badge ${row.action_type === 'delete' ? 'bg-danger' : row.action_type === 'create' ? 'bg-success' : 'bg-info'} text-capitalize`}>
                        {row.action_type || "-"}
                      </span>
                    </td>
                    <td>
                      {row.action_name
                        ? row.action_name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                        : "-"}
                    </td>
                    <td className="text-center">
                      {row.action_type === "update" && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setCompareRow(row);
                            setShowCompare(true);
                          }}
                        >
                          View Changes
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 🔹 PAGINATION */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
        <span className="text-secondary small">
          Showing <strong>{startEntry}</strong> to <strong>{endEntry}</strong> of <strong>{sortedRows.length}</strong> entries
        </span>

        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
            </li>
            {pages.map((p) => (
              <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
            </li>
          </ul>
        </nav>
      </div>

      {/* 🔹 COMPARE MODAL */}
      {showCompare && (
        <Compare
          show={showCompare}
          row={compareRow}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
}

export default Viewer;